import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useMemo, useCallback, useState, useEffect, useRef, Fragment } from 'react';
import { MapView, MapViewRef } from '../app/components/MapView';
import { LocationDetailSheet } from '../app/components/LocationDetailSheet';
import { ChargePointDetailSheet } from '../app/components/ChargePointDetailSheet';
import { MapBottomDrawer, type DrawerState } from '../app/components/MapBottomDrawer';
import { FilterPanel } from '../app/components/FilterPanel';
import { SupportModal } from '../app/components/SupportModal';
import { NearbyLocationPrompt } from '../app/components/NearbyLocationPrompt';
import { BalancePill } from '../app/components/BalancePill';
import { Search, MapPin, Navigation, SlidersHorizontal, Crosshair, Headphones, AlertCircle, X, RefreshCw, MapPinOff, LocateFixed, Heart, Clock, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '../app/components/ui/alert-dialog';
import { useLocationStore } from '../store/locationStore';
import { useVehicleStore } from '../store/vehicleStore';
import { useActiveChargingStore } from '../store/activeChargingStore';
import { getUserLocation, getInstantLocation, haversineDistance, type FallbackReason } from '../utils/geolocation';
import { toDetailedChargePoint } from '../utils/transformers';
import { applyFilters, hasActiveFilters, getAvailabilityStats } from '../utils/filters';
import { MAP_CONFIG, BRAND_COLORS } from '../constants';
import { useMapLocationStatus } from '../hooks/useMapLocationStatus';
import { toast } from 'sonner';
import { transactionService } from '../services/transactionService';
import { locationService } from '../services/locationService';
import { captureError } from '../lib/errorHandler';
import { addBreadcrumb } from '../lib/sentry';
import { useFavoritesStore } from '../store/favoritesStore';
import { useRecentSearchesStore } from '../store/recentSearchesStore';
import { transformLocations } from '../utils/transformers';
import type { ChargingLocation, Filters, ChargePoint } from '../types';

// Fallback location messages for user notification
const FALLBACK_MESSAGES: Record<FallbackReason, string> = {
  permission_denied: 'Location access denied. Showing stations near your last known area.',
  position_unavailable: 'Unable to determine your location. Showing nearby stations.',
  timeout: 'Location request timed out. Showing nearby stations.',
  unsupported: 'Location not supported on this device. Showing nearby stations.',
  unknown: 'Could not get your location. Showing nearby stations.',
};

/**
 * MapPage
 *
 * Main map view with location/chargepoint modals controlled by route parameters.
 *
 * Route Parameters:
 * - /app/map → Map view
 * - /app/location/:locationId → Location detail sheet
 * - /app/location/:locationId/chargepoint/:chargepointId → ChargePoint detail sheet
 *
 * Query Parameters (temporary UI state):
 * - ?filter=true → Opens filter panel
 */
export function MapPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locationId, chargepointId } = useParams();

  // Radius options for nearby search
  const RADIUS_OPTIONS = [10, 25, 50, 100] as const;
  const [searchRadius, setSearchRadius] = useState<number>(50);
  const searchRadiusRef = useRef(searchRadius);
  searchRadiusRef.current = searchRadius;

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChargingLocation[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [filters, setFilters] = useState<Filters>({
    connectorTypes: [],
    facilities: [],
    powerCategories: [],
    selectedVehicleId: undefined,
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>(MAP_CONFIG.defaultCenter);

  // Favorites and recents stores
  const { favorites, fetchFavorites, isFavorite } = useFavoritesStore();
  const { recents, addRecent, clearRecents } = useRecentSearchesStore();

  // Location permission rationale
  const [showLocationRationale, setShowLocationRationale] = useState(false);
  // Every exit from the rationale dialog must reach the system permission prompt
  // (App Store guideline 5.1.1(iv)), so there is only one action.
  const locationRationaleActionRef = useRef<'continue' | null>(null);

  // Loading timing control
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isLocatingGps, setIsLocatingGps] = useState(false);
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mapMoveDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const mapViewRef = useRef<MapViewRef>(null);

  // Ref to track last toast time for deduplication (prevents rapid-fire toasts)
  const lastFallbackToastTimeRef = useRef(0);

  // Parse query parameters (temporary UI state only)
  const showFilter = searchParams.has('filter');

  // Location store
  const {
    locations,
    isLoading,
    error,
    fetchNearbyLocations,
    fetchLocationById,
    clearError,
    userLocation,
    setUserLocation
  } = useLocationStore();

  // Vehicle store
  const { vehicles, fetchMyVehicles } = useVehicleStore();

  // Active charging store
  const setActiveSession = useActiveChargingStore((state) => state.setActiveSession);
  const hasActiveSession = useActiveChargingStore((state) => state.hasActiveSession);

  // State for loading a specific location (deep-link)
  const [isLoadingSpecificLocation, setIsLoadingSpecificLocation] = useState(false);

  // Real-time location status via SSE
  const { getEffectiveAvailability } = useMapLocationStatus({ enabled: true });

  // Memoized display status getter for MapView
  const getDisplayStatus = useCallback(
    (location: ChargingLocation) => getEffectiveAvailability(location).displayStatus,
    [getEffectiveAvailability]
  );

  // Apply filters to nearby locations (for map display)
  const displayedLocations = useMemo(() => {
    return applyFilters(locations, filters, vehicles);
  }, [locations, filters, vehicles]);

  // Apply filters to search results too
  const filteredSearchResults = useMemo(() => {
    if (searchResults.length === 0) return [];
    return applyFilters(searchResults, filters, vehicles);
  }, [searchResults, filters, vehicles]);

  const isFiltersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  // Debounced search via backend API
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (query.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const userLoc = useLocationStore.getState().userLocation;
        const results = await locationService.searchLocations({
          q: query.trim(),
          latitude: userLoc?.latitude,
          longitude: userLoc?.longitude,
          limit: 20,
          vehicleId: filters.selectedVehicleId,
        });
        const transformed = transformLocations(results as any);
        setSearchResults(transformed);
      } catch (err) {
        captureError(err, 'MapPage.searchLocations');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [filters.selectedVehicleId]);

  const { availableStations, totalChargepoints } = useMemo(
    () => getAvailabilityStats(locations),
    [locations]
  );

  // Find selected items from query params
  const selectedLocation = useMemo(
    () => (locationId ? locations.find((l) => l.id === locationId) : null),
    [locationId, locations]
  );

  const selectedChargepoint = useMemo(() => {
    if (!chargepointId || !selectedLocation) return null;
    const cp = selectedLocation.chargepoints.find((c) => c.id === chargepointId);
    return cp ? toDetailedChargePoint(cp) : null;
  }, [chargepointId, selectedLocation]);

  // Toast cooldown period in milliseconds (prevent duplicate toasts within this window)
  const TOAST_COOLDOWN_MS = 3000;

  // Helper to show fallback toast with deduplication
  const showFallbackToast = useCallback((reason: FallbackReason) => {
    const now = Date.now();
    if (now - lastFallbackToastTimeRef.current > TOAST_COOLDOWN_MS) {
      lastFallbackToastTimeRef.current = now;
      toast.info(FALLBACK_MESSAGES[reason], { duration: 5000 });
    }
  }, []);

  // Fetch locations on mount (with location rationale gate)
  // Uses instant (cached/default) coords to load the map immediately,
  // then resolves GPS in parallel and re-centers if a better fix arrives.
  useEffect(() => {
    let aborted = false;

    const loadLocations = async () => {
      if (hasLoadedOnce || isLoading) {
        return;
      }

      if (!localStorage.getItem('eve_location_rationale_shown')) {
        setShowLocationRationale(true);
        return;
      }

      try {
        const instant = getInstantLocation();
        const initialCoords = { latitude: instant.latitude, longitude: instant.longitude };

        setMapCenter([initialCoords.latitude, initialCoords.longitude]);
        setShowLoadingOverlay(true);

        setIsLocatingGps(true);
        const gpsPromise = getUserLocation()
          .then((result) => {
            recordFix(result);
            return result;
          })
          .finally(() => {
            setIsLocatingGps(false);
          });

        await fetchNearbyLocations({
          latitude: initialCoords.latitude,
          longitude: initialCoords.longitude,
          radius: searchRadiusRef.current,
          limit: 100,
        });

        setHasLoadedOnce(true);

        loadingTimerRef.current = setTimeout(() => {
          setShowLoadingOverlay(false);
        }, 300);

        gpsPromise.then(async (locationResult) => {
          if (aborted) return;

          if (locationResult.isFallback && locationResult.fallbackReason) {
            showFallbackToast(locationResult.fallbackReason);
            return;
          }

          const distKm = haversineDistance(
            initialCoords.latitude, initialCoords.longitude,
            locationResult.latitude, locationResult.longitude,
          );

          if (distKm > 1) {
            setMapCenter([locationResult.latitude, locationResult.longitude]);
            if (mapViewRef.current?.panTo) {
              mapViewRef.current.panTo([locationResult.latitude, locationResult.longitude]);
            }
            await fetchNearbyLocations({
              latitude: locationResult.latitude,
              longitude: locationResult.longitude,
              radius: searchRadiusRef.current,
              limit: 100,
            });
          }
        }).catch((err) => {
          if (!aborted) console.error('GPS resolution failed:', err);
        });

      } catch (err) {
        console.error('Failed to fetch locations:', err);
        setShowLoadingOverlay(false);
      }
    };

    loadLocations();

    return () => {
      aborted = true;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (mapMoveDebounceRef.current) {
        clearTimeout(mapMoveDebounceRef.current);
      }
    };
  }, [hasLoadedOnce, isLoading, fetchNearbyLocations, showFallbackToast]);

  // Resume location fetch after rationale dialog is dismissed
  useEffect(() => {
    if (showLocationRationale || hasLoadedOnce || !locationRationaleActionRef.current) {
      return;
    }

    locationRationaleActionRef.current = null;

    const resumeLoad = async () => {
      try {
        setShowLoadingOverlay(true);

        // Always request — getUserLocation() falls back to Kochi on denial.
        const locationResult = await getUserLocation();
        recordFix(locationResult);
        if (locationResult.isFallback && locationResult.fallbackReason) {
          showFallbackToast(locationResult.fallbackReason);
        }
        setMapCenter([locationResult.latitude, locationResult.longitude]);
        await fetchNearbyLocations({
          latitude: locationResult.latitude,
          longitude: locationResult.longitude,
          radius: searchRadius,
          limit: 100,
        });

        setHasLoadedOnce(true);
        loadingTimerRef.current = setTimeout(() => {
          setShowLoadingOverlay(false);
        }, 300);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        setShowLoadingOverlay(false);
      }
    };

    resumeLoad();
  }, [showLocationRationale, hasLoadedOnce, fetchNearbyLocations, showFallbackToast, searchRadius]);

  // Fetch specific location by ID for deep-link access
  useEffect(() => {
    const fetchSpecificLocation = async () => {
      // If we have a locationId but no matching location, try to fetch it
      if (locationId && !selectedLocation && !isLoadingSpecificLocation) {
        // Only attempt fetch if:
        // 1. We've done initial load (nearby locations)
        // 2. OR there are no locations yet (direct deep-link access before nearby fetch)
        if (hasLoadedOnce || locations.length === 0) {
          setIsLoadingSpecificLocation(true);
          try {
            const fetchedLocation = await fetchLocationById(locationId);
            if (!fetchedLocation) {
              toast.error('Location not found');
              navigate('/app/map', { replace: true });
            }
          } catch (err) {
            console.error('[MapPage] Failed to fetch location by ID:', err);
            toast.error('Failed to load location. Please try again.');
            navigate('/app/map', { replace: true });
          } finally {
            setIsLoadingSpecificLocation(false);
          }
        }
      }
    };

    fetchSpecificLocation();
  }, [locationId, selectedLocation, hasLoadedOnce, locations.length, fetchLocationById, navigate, isLoadingSpecificLocation]);

  // Validate chargepoint URL parameter after location is loaded
  useEffect(() => {
    // Only check chargepoint after location is loaded
    if (chargepointId && selectedLocation && !selectedChargepoint) {
      toast.error('Charge point not found');
      navigate(`/app/location/${locationId}`, { replace: true });
    }
  }, [chargepointId, selectedLocation, selectedChargepoint, locationId, navigate]);

  // Fetch vehicles and favorites on mount
  useEffect(() => {
    fetchMyVehicles();
    fetchFavorites();
  }, [fetchMyVehicles, fetchFavorites]);

  // Closing the rationale always continues to the system permission request.
  const dismissLocationRationale = useCallback(() => {
    localStorage.setItem('eve_location_rationale_shown', 'true');
    locationRationaleActionRef.current = 'continue';
    setShowLocationRationale(false);
  }, []);

  // Retry handler for error recovery
  const handleRetry = useCallback(async (overrideRadius?: number) => {
    clearError();

    try {
      // Show loading overlay
      setShowLoadingOverlay(true);

      const locationResult = await getUserLocation();
      recordFix(locationResult);

      // Notify user if using fallback location (with deduplication)
      if (locationResult.isFallback && locationResult.fallbackReason) {
        showFallbackToast(locationResult.fallbackReason);
      }

      // Update map center to user's location (or fallback)
      setMapCenter([locationResult.latitude, locationResult.longitude]);

      await fetchNearbyLocations({
        latitude: locationResult.latitude,
        longitude: locationResult.longitude,
        radius: overrideRadius ?? searchRadius,
        limit: 100,
      });

      // Keep loading overlay visible for minimum 300ms
      loadingTimerRef.current = setTimeout(() => {
        setShowLoadingOverlay(false);
      }, 300);

    } catch (err) {
      console.error('Retry failed:', err);
      setShowLoadingOverlay(false);
    }
  }, [clearError, fetchNearbyLocations, showFallbackToast, searchRadius]);

  // Handle map move with debouncing
  const handleMapMove = useCallback(
    (center: { latitude: number; longitude: number }) => {
      // Clear any pending debounce timer
      if (mapMoveDebounceRef.current) {
        clearTimeout(mapMoveDebounceRef.current);
      }

      // Debounce location fetch by 800ms to prevent excessive API calls
      mapMoveDebounceRef.current = setTimeout(async () => {
        try {
          await fetchNearbyLocations({
            latitude: center.latitude,
            longitude: center.longitude,
            radius: searchRadius,
            limit: 100,
          });
        } catch (err) {
          console.error('Failed to fetch locations after map move:', err);
        }
      }, 800);
    },
    [fetchNearbyLocations, searchRadius]
  );

  // Event handlers - Using route-based navigation
  const openLocation = useCallback(
    (location: ChargingLocation) => {
      navigate(`/app/location/${location.id}`);
      setSearchQuery(''); // Clear search when opening location
    },
    [navigate]
  );

  const closeModals = useCallback(() => {
    navigate('/app/map');
  }, [navigate]);

  const backToLocation = useCallback(() => {
    if (locationId) {
      navigate(`/app/location/${locationId}`);
    }
  }, [locationId, navigate]);

  const handleSearchSelect = useCallback(
    (location: ChargingLocation) => {
      addRecent({ id: location.id, name: location.name, city: location.city || '' });
      setSearchQuery('');
      setSearchResults([]);
      setIsSearchFocused(false);

      // Pan map to the selected location
      const newCenter: [number, number] = location.position;
      setMapCenter(newCenter);
      if (mapViewRef.current?.panTo) {
        mapViewRef.current.panTo(newCenter);
      }

      openLocation(location);
    },
    [openLocation, addRecent]
  );

  const handleRecentSelect = useCallback(
    async (locationId: string) => {
      setSearchQuery('');
      setSearchResults([]);
      setIsSearchFocused(false);
      try {
        const fetched = await fetchLocationById(locationId);
        if (fetched) {
          const newCenter: [number, number] = fetched.position;
          setMapCenter(newCenter);
          if (mapViewRef.current?.panTo) {
            mapViewRef.current.panTo(newCenter);
          }
          navigate(`/app/location/${locationId}`);
        } else {
          toast.error('Location not found');
        }
      } catch {
        toast.error('Failed to load location');
      }
    },
    [fetchLocationById, navigate]
  );

  const handleCenterToCurrentLocation = useCallback(async () => {
    try {
      setIsLocatingGps(true);
      const locationResult = await getUserLocation();
      recordFix(locationResult);
      const newCenter: [number, number] = [locationResult.latitude, locationResult.longitude];

      if (locationResult.isFallback && locationResult.fallbackReason) {
        showFallbackToast(locationResult.fallbackReason);
      }

      setMapCenter(newCenter);

      if (mapViewRef.current?.panTo) {
        mapViewRef.current.panTo(newCenter);
      }
    } catch (err) {
      captureError(err, 'MapPage.geolocation');
      toast.error('Unable to get your current location. Please check your location permissions.');
    } finally {
      setIsLocatingGps(false);
    }
  }, [showFallbackToast]);

  const handleRadiusChange = useCallback((radius: number) => {
    setSearchRadius(radius);
    handleRetry(radius);
  }, [handleRetry]);

  const handleSupportClick = useCallback(() => {
    setShowSupport(true);
  }, []);

  // State for support modal
  const [showSupport, setShowSupport] = useState(false);

  // "You're at X" prompt. Proximity is only claimed on a genuine fix that the
  // device says is good to ~50m; a network-derived fix comes back with
  // isFallback false and can be hundreds of metres out, so accuracy is checked
  // rather than trusted. Distance is computed here rather than read from
  // location.distance, which the backend fills in on the nearby query and leaves
  // at 0 when a location is fetched by id — the deep-link and QR paths.
  const recordFix = useCallback(
    (result: { latitude: number; longitude: number; isFallback: boolean; accuracy?: number }) => {
      if (result.isFallback) return;
      setUserLocation(result.latitude, result.longitude, result.accuracy);
    },
    [setUserLocation]
  );

  const NEARBY_RADIUS_M = 75;
  const NEARBY_MAX_ACCURACY_M = 50;
  const [dismissedNearbyId, setDismissedNearbyId] = useState<string | null>(null);

  const nearbyPrompt = useMemo(() => {
    const fix = userLocation;
    if (!fix || fix.accuracy == null || fix.accuracy > NEARBY_MAX_ACCURACY_M) return null;
    if (hasActiveSession) return null;

    let best: { location: ChargingLocation; metres: number } | null = null;
    for (const candidate of locations) {
      const metres =
        haversineDistance(fix.latitude, fix.longitude, candidate.position[0], candidate.position[1]) * 1000;
      if (metres <= NEARBY_RADIUS_M && (!best || metres < best.metres)) {
        best = { location: candidate, metres };
      }
    }
    if (!best || best.location.id === dismissedNearbyId) return null;

    const availability = getEffectiveAvailability(best.location);
    if (availability.totalCount === 0) return null;

    return { location: best.location, ...availability };
  }, [userLocation, locations, dismissedNearbyId, hasActiveSession, getEffectiveAvailability]);

  // Track drawer state for FAB positioning
  const [drawerState, setDrawerState] = useState<DrawerState>('minimized');

  const [isStartingCharge, setIsStartingCharge] = useState(false);
  const startingChargeRef = useRef(false);

  // Handle start charging with API call
  const handleStartCharging = useCallback(async (chargePointId: string, connectorId: string) => {
    if (startingChargeRef.current) return;
    startingChargeRef.current = true;
    setIsStartingCharge(true);
    try {
      const response = await transactionService.startCharging({ chargePointId, connectorId });

      // The backend returns transactionId in either id or transactionId field
      const transactionId = response.transactionId || response.id;

      if (transactionId) {
        // Fetch full transaction details to populate active session
        const transaction = await transactionService.getTransactionById(transactionId);

        // Update active charging store BEFORE navigation
        setActiveSession({
          transactionId: transaction.id,
          chargePointId: transaction.chargePointId,
          connectorId: transaction.connectorId,
          locationName: transaction.chargePoint?.location?.name,
          chargePointName: transaction.chargePoint?.name,
          batteryLevel: transaction.currentSoc ?? transaction.currentBattery ?? 0,
          currentPower: transaction.currentPowerKw ?? 0,
          energyConsumed: transaction.totalKwh ?? transaction.energyUsed ?? 0,
          startedAt: new Date(transaction.createdAt),
        });

        // Navigate to activity page with charging session info
        navigate(`/app/activity/charging/${transactionId}`, {
          state: { chargePointId, connectorId },
        });
      } else {
        toast.error(response.message || 'Failed to start charging');
      }
    } catch (error: any) {
      console.error('[MapPage] Failed to start charging:', error);
      addBreadcrumb('charging', 'Start charging failed', { chargePointId, connectorId });
      toast.error(error.response?.data?.message || 'Failed to start charging session');
    } finally {
      startingChargeRef.current = false;
      setIsStartingCharge(false);
    }
  }, [navigate, setActiveSession]);

  return (
    <>
      {/* Loading Overlay - Initial Load with Fade Transition */}
      {showLoadingOverlay && (
        <div
          className="absolute inset-0 z-[2000] bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm flex items-center justify-center transition-opacity duration-300 ease-in-out"
          style={{ opacity: showLoadingOverlay ? 1 : 0 }}
        >
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#8bc43e] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 dark:text-gray-200 font-medium text-lg">Finding nearby stations...</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Please wait</p>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="absolute top-20 left-4 right-4 z-[1001] bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900 mb-1">Unable to load locations</p>
              <p className="text-xs text-red-700">{error}</p>
              <p className="text-xs text-red-600 mt-2">Please check your internet connection and try again</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => handleRetry()}
                className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded bg-red-100 hover:bg-red-200"
              >
                Retry
              </button>
              <button
                onClick={clearError}
                className="text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Header with Search — positioned against <main> inside
          ProtectedLayout, which already carries pt-[env(safe-area-inset-top)].
          Do NOT add the inset here; it double-counts and drops the card ~59pt. */}
      <div className="absolute top-4 left-4 right-4 z-[998] bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-gray-100 dark:border-gray-700">
        {/* EVee Buddy Branding */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="45" stroke={BRAND_COLORS.primary} strokeWidth="7" />
              <rect x="32" y="30" width="10" height="20" rx="1" fill={BRAND_COLORS.primary} />
              <rect x="58" y="30" width="10" height="20" rx="1" fill={BRAND_COLORS.primary} />
              <rect x="41" y="58" width="18" height="12" rx="1" fill={BRAND_COLORS.primary} />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 dark:text-white">EVee Buddy</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">for a worry free travel!</p>
          </div>

        </div>

        {/* Search Box */}
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            {isSearching ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8bc43e] animate-spin" aria-hidden="true" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            )}
            <input
              type="text"
              placeholder="Search stations, cities..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8bc43e] focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              aria-label="Search charging stations"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => handleRetry()}
            disabled={showLoadingOverlay || isLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
              showLoadingOverlay || isLoading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 shadow-sm'
            }`}
            title="Refresh locations"
            aria-label="Refresh locations"
          >
            <RefreshCw className={`w-5 h-5 ${showLoadingOverlay || isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>

          {/* Filter Button */}
          <button
            className="bg-gray-50 dark:bg-gray-700 rounded-xl shadow-md p-2.5 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors relative flex-shrink-0 border border-gray-200 dark:border-gray-600"
            onClick={() => setSearchParams((prev) => ({ ...prev, filter: 'true' }))}
            aria-label="Open filters"
          >
            <SlidersHorizontal className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            {isFiltersActive && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#8bc43e] rounded-full flex items-center justify-center text-white text-xs font-bold">
                {filters.connectorTypes.length + filters.facilities.length + filters.powerCategories.length}
              </span>
            )}
          </button>

          {/* Search Dropdown: Favorites + Recents (empty query, focused) */}
          {isSearchFocused && !searchQuery.trim() && (favorites.length > 0 || recents.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden max-h-72 overflow-y-auto z-10">
              {/* Favorites Section */}
              {favorites.length > 0 && (
                <>
                  <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
                    <Heart className="w-3.5 h-3.5 text-red-400" fill="currentColor" />
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Favorites</span>
                  </div>
                  {favorites.map((location) => (
                    <button
                      key={`fav-${location.id}`}
                      onClick={() => handleSearchSelect(location)}
                      className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <MapPin className="w-4 h-4 text-[#8bc43e] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{location.name}</p>
                        {location.city && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">{location.city}{location.distance ? ` · ${location.distance} km` : ''}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </>
              )}

              {/* Recents Section */}
              {recents.length > 0 && (
                <>
                  <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Recent</span>
                    </div>
                    <button onClick={clearRecents} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      Clear
                    </button>
                  </div>
                  {recents.map((recent) => (
                    <button
                      key={`recent-${recent.locationId}`}
                      onClick={() => handleRecentSelect(recent.locationId)}
                      className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <Clock className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{recent.name}</p>
                        {recent.city && <p className="text-xs text-gray-400 dark:text-gray-500">{recent.city}</p>}
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Search Results Dropdown (has query) */}
          {searchQuery.trim().length >= 2 && !isSearching && filteredSearchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden max-h-64 overflow-y-auto z-10">
              {filteredSearchResults.map((location) => {
                const availability = getEffectiveAvailability(location);
                const { displayStatus, availableCount, totalCount } = availability;

                const statusBgColors = {
                  available: 'bg-green-100 dark:bg-green-900',
                  occupied: 'bg-amber-100 dark:bg-amber-900',
                  offline: 'bg-gray-100 dark:bg-gray-700',
                };
                const statusTextColors = {
                  available: 'text-green-600 dark:text-green-400',
                  occupied: 'text-amber-600 dark:text-amber-400',
                  offline: 'text-gray-500 dark:text-gray-400',
                };

                const distanceDisplay = location.distance != null
                  ? location.distance >= 1 ? `${Math.round(location.distance)} km` : `${Math.round(location.distance * 1000)} m`
                  : null;

                return (
                  <button
                    key={location.id}
                    onClick={() => handleSearchSelect(location)}
                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-50 dark:border-gray-700 last:border-b-0 text-left"
                  >
                    <div
                      className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${statusBgColors[displayStatus]}`}
                    >
                      <MapPin
                        className={`w-4 h-4 ${statusTextColors[displayStatus]}`}
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {location.name}
                        {location.city && <span className="text-gray-400 font-normal">, {location.city}</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {distanceDisplay && (
                          <>
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Navigation className="w-3 h-3" aria-hidden="true" />
                              {distanceDisplay}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500" aria-hidden="true">·</span>
                          </>
                        )}
                        <span className={`text-sm ${statusTextColors[displayStatus]}`}>
                          {displayStatus === 'offline'
                            ? 'Offline'
                            : `${availableCount}/${totalCount} available`}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* No results message */}
          {searchQuery.trim().length >= 2 && !isSearching && filteredSearchResults.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 px-4 py-6 text-center">
              <p className="text-gray-500 dark:text-gray-400 text-sm">No charging stations found</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">Try a different search term</p>
            </div>
          )}
        </div>

        {/* Radius strip */}
        <div className="flex items-center gap-1.5 mt-2 justify-center">
          <Crosshair className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
          <span className="text-[10px] text-gray-300 dark:text-gray-600 select-none mr-0.5">within</span>
          {RADIUS_OPTIONS.map((r, i) => (
            <Fragment key={r}>
              {i > 0 && (
                <span className="text-[10px] text-gray-200 dark:text-gray-700 select-none">·</span>
              )}
              <button
                onClick={() => handleRadiusChange(r)}
                className={`text-[11px] transition-colors ${
                  searchRadius === r
                    ? 'text-[#8bc43e] font-semibold'
                    : 'text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500'
                }`}
              >
                {r}
              </button>
            </Fragment>
          ))}
          <span className="text-[10px] text-gray-300 dark:text-gray-600 ml-0.5 select-none">km</span>
        </div>

        {/* Hangs off the bottom of the header rather than floating over the map,
            which is where the QR and support buttons live. Hidden while a search
            dropdown is open, since that occupies the same space. */}
        {nearbyPrompt && !locationId && !isSearchFocused && (
          <NearbyLocationPrompt
            location={nearbyPrompt.location}
            onView={() => openLocation(nearbyPrompt.location)}
            onDismiss={() => setDismissedNearbyId(nearbyPrompt.location.id)}
          />
        )}
      </div>

      {/* Map View */}
      <MapView
        ref={mapViewRef}
        locations={displayedLocations}
        onLocationClick={openLocation}
        center={mapCenter}
        onMapMove={handleMapMove}
        getDisplayStatus={getDisplayStatus}
      />

      {/* Floating Balance - left side, fixed position above minimized drawer */}
      {!locationId && drawerState !== 'full' && (
        <div
          className="fixed left-4 z-[998]"
          style={{
            bottom: 212,
          }}
        >
          <BalancePill />
        </div>
      )}

      {/* Floating Action Buttons - hidden when sheet is open or drawer is full */}
      {!locationId && drawerState !== 'full' && (
        <div
          className="fixed right-4 z-[998] flex flex-col gap-3"
          style={{
            bottom: 212,
          }}
        >
          {/* My Location FAB */}
          <button
            onClick={handleCenterToCurrentLocation}
            disabled={isLocatingGps}
            className="relative bg-white dark:bg-gray-800 rounded-full shadow-xl p-4 hover:shadow-2xl transition-all duration-200 active:scale-95 border border-gray-200 dark:border-gray-700"
            aria-label="Center to my location"
          >
            {isLocatingGps && (
              <span className="absolute inset-0 rounded-full border-2 border-[#8bc43e] animate-ping opacity-50" />
            )}
            {isLocatingGps ? (
              <Loader2 className="w-6 h-6 text-[#8bc43e] animate-spin" aria-hidden="true" />
            ) : (
              <Crosshair className="w-6 h-6 text-gray-700 dark:text-gray-300" aria-hidden="true" />
            )}
          </button>

          {/* Support FAB */}
          <button
            onClick={handleSupportClick}
            className="bg-white dark:bg-gray-800 rounded-full shadow-xl p-4 hover:shadow-2xl transition-all duration-200 active:scale-95 border border-gray-200 dark:border-gray-700"
            aria-label="Contact support"
          >
            <Headphones className="w-6 h-6 text-gray-700 dark:text-gray-300" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Bottom Drawer - Only visible when no sheet is open */}
      {!locationId && !chargepointId && (
        <>
          {/* Empty State - No Locations */}
          {!showLoadingOverlay && !isLoading && locations.length === 0 && !error ? (
            <div className="absolute bottom-6 left-4 right-[84px] z-[999] bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 px-4 py-3">
              <div className="flex items-center gap-3">
                <MapPinOff className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 dark:text-gray-300">No stations within {searchRadius} km</p>
                  {RADIUS_OPTIONS[RADIUS_OPTIONS.length - 1] !== searchRadius && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Try expanding the search radius</p>
                  )}
                </div>
                {(() => {
                  const nextRadius = RADIUS_OPTIONS[RADIUS_OPTIONS.indexOf(searchRadius as typeof RADIUS_OPTIONS[number]) + 1];
                  return nextRadius ? (
                    <button
                      onClick={() => handleRadiusChange(nextRadius)}
                      className="px-3 py-1.5 bg-[#8bc43e] text-white text-xs font-medium rounded-lg hover:bg-[#7ab32e] transition-colors flex-shrink-0"
                    >
                      Try {nextRadius} km
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRetry()}
                      className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                    >
                      Retry
                    </button>
                  );
                })()}
              </div>
            </div>
          ) : (
            <MapBottomDrawer locations={displayedLocations} onLocationClick={openLocation} hasChargingBottomNav={false} getEffectiveAvailability={getEffectiveAvailability} onDrawerStateChange={setDrawerState} />
          )}

        </>
      )}

      {/* Modals controlled by route params */}
      <LocationDetailSheet
        location={selectedLocation ?? null}
        open={!!locationId && !chargepointId}
        onOpenChange={(open) => !open && navigate('/app/map')}
        onStartCharging={handleStartCharging}
        isStartingCharge={isStartingCharge}
      />

      <ChargePointDetailSheet
        chargepoint={selectedChargepoint}
        location={selectedLocation}
        open={!!chargepointId}
        onOpenChange={(open) => !open && locationId && navigate(`/app/location/${locationId}`)}
        onBack={() => locationId && navigate(`/app/location/${locationId}`)}
        onStartCharging={handleStartCharging}
        isStartingCharge={isStartingCharge}
        scannedGun={searchParams.get('gun') ?? undefined}
        isLoading={!hasLoadedOnce || showLoadingOverlay || isLoadingSpecificLocation}
      />

      <FilterPanel
        open={showFilter}
        onOpenChange={(open) => {
          if (!open) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('filter');
            setSearchParams(newParams);
          }
        }}
        filters={filters}
        onFiltersChange={setFilters}
        vehicles={vehicles}
      />

      <SupportModal open={showSupport} onOpenChange={setShowSupport} />

      {/* Location Permission Rationale Dialog */}
      <AlertDialog open={showLocationRationale} onOpenChange={(open) => {
        // Dismissing (Escape/overlay) proceeds to the permission request too —
        // the system alert is the only place to decline.
        if (!open) {
          dismissLocationRationale();
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 bg-[#8bc43e]/10 rounded-full flex items-center justify-center">
                <LocateFixed className="w-7 h-7 text-[#8bc43e]" />
              </div>
            </div>
            <AlertDialogTitle className="text-center">Location Access</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              EVee Buddy uses your location to find nearby charging stations. Your location is not stored or shared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={dismissLocationRationale}
              className="bg-[#8bc43e] hover:bg-[#7ab32e]"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
